// Multi-selection over the rows currently shown in the main pane. A single selection is the keyboard
// cursor (`selectedTaskId`); ⌘/Ctrl-click toggles rows into `selectedIds`, ⇧-click and ⇧↓/↑ select
// a range from the anchor, ⌘A selects every visible row.
import { useEffect } from "react";
import type { TaskWithFlags } from "@desk/contracts";
import { taskRegistry } from "@/features/list/registry";
import { ui } from "@/features/ui-state";

/** Task ids in on-screen order (rows that are rendered and visible). */
export function visibleRowIds(): string[] {
  const root = document.getElementById("desk-main-scroll") ?? document;
  return [...root.querySelectorAll<HTMLElement>("[data-task-id]")]
    .filter((el) => el.offsetParent !== null)
    .map((el) => el.dataset.taskId!)
    .filter((id, i, arr) => arr.indexOf(id) === i);
}

/** Ids from `a` to `b` inclusive, in list order. */
export function rangeBetween(ids: string[], a: string, b: string): string[] {
  const i = ids.indexOf(a);
  const j = ids.indexOf(b);
  if (i === -1 || j === -1) return [b];
  return ids.slice(Math.min(i, j), Math.max(i, j) + 1);
}

/** Toggle `id` in a selection that may currently be just the cursor row. */
export function toggled(current: string[], cursor: string | null, id: string): string[] {
  const base = current.length ? current : cursor ? [cursor] : [];
  return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
}

export function selectSingle(id: string) {
  ui.set({ selectedTaskId: id, selectedIds: [], selectionAnchor: id });
}

export function clearSelection() {
  ui.set({ selectedIds: [], selectionAnchor: null });
}

/** An async action may change its starting selection only while the user still owns that selection. */
export function captureSelection() {
  const before = ui.get();
  return () => {
    const now = ui.get();
    return now.selectedIds === before.selectedIds && now.selectedTaskId === before.selectedTaskId && now.selectionAnchor === before.selectionAnchor;
  };
}

/** Drop rows that left the view, keeping the remaining cursor and range anchor consistent. */
export function reconcileSelection(ids: readonly string[] = [...taskRegistry.keys()]) {
  const visible = new Set(ids);
  const s = ui.get();
  const selectedIds = s.selectedIds.filter((id) => visible.has(id));
  const selectedTaskId = s.selectedTaskId && visible.has(s.selectedTaskId) ? s.selectedTaskId : selectedIds.at(-1) ?? null;
  const selectionAnchor = s.selectionAnchor === null ? null : visible.has(s.selectionAnchor) ? s.selectionAnchor : selectedIds[0] ?? selectedTaskId;
  if (selectedIds.length !== s.selectedIds.length || selectedTaskId !== s.selectedTaskId || selectionAnchor !== s.selectionAnchor)
    ui.set({ selectedIds, selectedTaskId, selectionAnchor });
}

/** The persistent main pane owns visibility, including when its final list unmounts. */
export function useReconcileSelection(scope?: string) {
  useEffect(() => {
    const root = document.getElementById("desk-main-scroll");
    if (!root) return;
    const sync = () => reconcileSelection(visibleRowIds());
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden", "data-task-id"] });
    return () => observer.disconnect();
  }, [scope]);
}

/** Apply a modified click. Returns false for a plain click (the caller selects/open as usual). */
export function modifierSelect(id: string, e: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean }): boolean {
  const s = ui.get();
  if (e.shiftKey) {
    const anchor = s.selectionAnchor ?? s.selectedTaskId ?? id;
    ui.set({ selectedIds: rangeBetween(visibleRowIds(), anchor, id), selectedTaskId: id, selectionAnchor: anchor });
    return true;
  }
  if (e.metaKey || e.ctrlKey) {
    const next = toggled(s.selectedIds, s.selectedTaskId, id);
    ui.set({ selectedIds: next, selectedTaskId: next.includes(id) ? id : (next.at(-1) ?? null), selectionAnchor: id });
    return true;
  }
  return false;
}

/** ⇧↓/⇧↑: grow or shrink the range from the anchor by one row. */
export function extendSelection(down: boolean) {
  const s = ui.get();
  const ids = visibleRowIds();
  if (!ids.length) return;
  const cursor = s.selectedTaskId ?? ids[down ? 0 : ids.length - 1]!;
  const anchor = s.selectionAnchor ?? cursor;
  const i = Math.max(0, Math.min(ids.length - 1, ids.indexOf(cursor) + (down ? 1 : -1)));
  ui.set({ selectedTaskId: ids[i]!, selectedIds: rangeBetween(ids, anchor, ids[i]!), selectionAnchor: anchor });
  return ids[i];
}

export function selectAllVisible() {
  const ids = visibleRowIds();
  if (ids.length) ui.set({ selectedIds: ids, selectionAnchor: ids[0]!, selectedTaskId: ids.at(-1)! });
}

/** The selected tasks that are on screen, in selection order. */
export function selectedTasks(): TaskWithFlags[] {
  return ui
    .get()
    .selectedIds.map((id) => taskRegistry.get(id))
    .filter((t): t is TaskWithFlags => !!t);
}
